import posthog from "posthog-js";

export const DEMAND_STRIP_VIEWED_EVENT = "demand_strip_viewed" as const;
export const DEMAND_STRIP_I_HAVE_THIS_CLICKED_EVENT =
  "demand_strip_i_have_this_clicked" as const;
export const DEMAND_STRIP_GROUP_EXPANDED_EVENT =
  "demand_strip_group_expanded" as const;
export const DEMAND_STRIP_REQUEST_CTA_CLICKED_EVENT =
  "demand_strip_request_cta_clicked" as const;
export const SELL_FROM_REQUEST_LANDED_EVENT =
  "sell_from_request_landed" as const;

export type DemandStripEntryKind = "group" | "chip" | "nested";
export type DemandStripRequestCtaSurface = "header" | "empty";

export type CaptureDemandStripViewedParams = {
  entryCount: number;
  groupCount: number;
  chipCount: number;
  hasMore: boolean;
};

export type CaptureDemandStripIHaveThisClickedParams = {
  itemRequestId: string;
  entryKind: DemandStripEntryKind;
  category: string;
  subcategory?: string | null;
  groupSize?: number | null;
  prefsDiverged: boolean;
  showGroupIHaveThis: boolean;
};

export type CaptureDemandStripGroupExpandedParams = {
  subcategory: string;
  groupSize: number;
  hadDirectCta: boolean;
};

export type CaptureDemandStripRequestCtaClickedParams = {
  surface: DemandStripRequestCtaSurface;
};

export type CaptureSellFromRequestLandedParams = {
  itemRequestId: string | null;
  category: string;
  subcategory?: string | null;
  listingMode: "single" | "bulk";
};

/** Props to merge into listing_submitted / bulk_listing_submitted. */
export function buildListingSubmittedFromRequestProps(params: {
  fromRequest: boolean;
  itemRequestId?: string | null;
}): Record<string, unknown> {
  if (!params.fromRequest) {
    return { from_request: false };
  }
  return {
    from_request: true,
    item_request_id: params.itemRequestId || null,
  };
}

function warnCapture(event: string, error: unknown): void {
  console.warn(`[posthog] ${event} capture failed:`, error);
}

/**
 * Homepage demand strip: inventory impression (once per mount with rows).
 * Baseline: artifacts/demand-strip-analytics-baseline.md
 */
export function captureDemandStripViewed(
  params: CaptureDemandStripViewedParams,
): void {
  try {
    posthog.capture(DEMAND_STRIP_VIEWED_EVENT, {
      entry_count: params.entryCount,
      group_count: params.groupCount,
      chip_count: params.chipCount,
      has_more: params.hasMore,
    });
  } catch (error) {
    warnCapture(DEMAND_STRIP_VIEWED_EVENT, error);
  }
}

/** Seller taps I have this from the demand strip. */
export function captureDemandStripIHaveThisClicked(
  params: CaptureDemandStripIHaveThisClickedParams,
): void {
  try {
    posthog.capture(DEMAND_STRIP_I_HAVE_THIS_CLICKED_EVENT, {
      item_request_id: params.itemRequestId,
      entry_kind: params.entryKind,
      category: params.category,
      subcategory: params.subcategory || null,
      group_size: params.groupSize ?? null,
      prefs_diverged: params.prefsDiverged,
      show_group_i_have_this: params.showGroupIHaveThis,
    });
  } catch (error) {
    warnCapture(DEMAND_STRIP_I_HAVE_THIS_CLICKED_EVENT, error);
  }
}

/** Seller expands a group (See options / Pick a match). */
export function captureDemandStripGroupExpanded(
  params: CaptureDemandStripGroupExpandedParams,
): void {
  try {
    posthog.capture(DEMAND_STRIP_GROUP_EXPANDED_EVENT, {
      subcategory: params.subcategory,
      group_size: params.groupSize,
      had_direct_cta: params.hadDirectCta,
    });
  } catch (error) {
    warnCapture(DEMAND_STRIP_GROUP_EXPANDED_EVENT, error);
  }
}

/** Buyer taps Request an item from strip header or empty state. */
export function captureDemandStripRequestCtaClicked(
  params: CaptureDemandStripRequestCtaClickedParams,
): void {
  try {
    posthog.capture(DEMAND_STRIP_REQUEST_CTA_CLICKED_EVENT, {
      surface: params.surface,
    });
  } catch (error) {
    warnCapture(DEMAND_STRIP_REQUEST_CTA_CLICKED_EVENT, error);
  }
}

/** Sell page applied fromRequest prefill (funnel mid-step). */
export function captureSellFromRequestLanded(
  params: CaptureSellFromRequestLandedParams,
): void {
  try {
    posthog.capture(SELL_FROM_REQUEST_LANDED_EVENT, {
      item_request_id: params.itemRequestId || null,
      category: params.category,
      subcategory: params.subcategory || null,
      listing_mode: params.listingMode,
    });
  } catch (error) {
    warnCapture(SELL_FROM_REQUEST_LANDED_EVENT, error);
  }
}
